import { ReactNode, RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getStoredSession, logoutCurrentSession, logoutFailureMessage } from "../../api/auth";
import {
  getTeacherLinkBinding,
  getTeacherWebComparisonRunStatus,
  getTeacherWebGlbDownloadUrl,
  getTeacherWebLlmStatus,
  getTeacherWebResultAssetUrls,
  TeacherWebComparisonRunStatus,
  TeacherWebCompData,
  TeacherWebGlbDownload,
  TeacherWebLlmAnalysis,
  TeacherWebLlmStatus,
  TeacherWebQwenDescription,
  TeacherWebResultAssetName,
  TeacherWebResultAssetUrl,
} from "../../api/teacherWeb";
import {
  defaultOrbitDistance,
  GlbPreview,
  maximumOrbitDistance,
  minimumOrbitDistance,
  orbitDistanceStep,
  supportsWebGLRendering,
  type GlbViewCommand,
} from "../../three/GlbPreview";
import { loadGlbObjectUrlFromRemote } from "../../utils/glbFile";
import { runAdaptivePolling } from "../../utils/adaptivePolling";
import { clearActiveAnalysisRun } from "../upload/uploadDraft";
import { LogoutConfirmDialog } from "../../components/LogoutConfirmDialog";
import { ComparisonOverview } from "./ComparisonOverview";
import { ComparisonViewControls } from "./ComparisonViewControls";
import { calculateComparisonStandardness } from "./standardness";

type ModelSlot = "first" | "second";
type ResultDetailTab = "overview" | "data" | "insight" | "control";
type LlmViewStatus = "idle" | "pending" | "completed" | "failed" | "error";

type ChartDefinition = {
  description: string;
  key: string;
  name: string;
  shortName: string;
  unit: string;
};

type ChartItem = {
  definition: ChartDefinition;
  keyFramesFirst: number[];
  keyFramesSecond: number[];
  maxFrameCount: number;
  seriesFirst: ChartSeries | null;
  seriesSecond: ChartSeries | null;
};

type ChartSeries = {
  mean: number[] | null;
  original: number[];
  standardDeviation: number[] | null;
};

const lastTeacherTokenKey = "pingpi.teacherWeb.lastTeacherToken";
const modelSlots: ModelSlot[] = ["first", "second"];

const chartDefinitions: ChartDefinition[] = [
  {
    key: "elbow_angle_right",
    name: "肘部角度",
    shortName: "肘角",
    unit: "度",
    description: "衡量手臂弯曲程度的角度，角度越小表示手臂弯曲越多，角度越大表示手臂越伸直。",
  },
  {
    key: "shoulder_hip_angle_right",
    name: "肩髋角度",
    shortName: "肩髋角",
    unit: "度",
    description: "反映大臂相对躯干的抬高程度，角度越大表示大臂抬得越高，角度越小表示大臂越贴近身体。",
  },
  {
    key: "shoulder_arm_angle_right",
    name: "肩臂角度",
    shortName: "肩臂角",
    unit: "度",
    description: "衡量大臂相对肩线的外展程度，角度越小表示手臂越贴近身体中线，角度越大表示手臂越向外展开。",
  },
  {
    key: "shoulder_rotation_right",
    name: "肩部旋转角度",
    shortName: "肩转角",
    unit: "度",
    description: "反映击球过程中肩部的转动幅度，正值表示肩向前转动（随挥），负值表示肩向后转动（引拍）。",
  },
  {
    key: "hip_angle_right",
    name: "髋部角度",
    shortName: "髋角",
    unit: "度",
    description: "衡量髋关节的弯曲程度，角度越小表示身体越弯曲，角度越大表示身体越直立。",
  },
  {
    key: "hip_rotation_right",
    name: "髋部旋转角度",
    shortName: "髋转角",
    unit: "度",
    description: "反映击球过程中髋部的转动幅度，正值表示髋向前转动（蹬转发力），负值表示髋向后转动（引拍蓄力）。",
  },
  {
    key: "knee_angle_right",
    name: "膝盖角度",
    shortName: "膝角",
    unit: "度",
    description: "衡量膝盖弯曲程度的角度，角度越小表示下蹲越深，角度越大表示腿部越伸直。",
  },
  {
    key: "elbow_angle_left",
    name: "肘部角度",
    shortName: "肘角",
    unit: "度",
    description: "衡量手臂弯曲程度的角度，角度越小表示手臂弯曲越多，角度越大表示手臂越伸直。",
  },
  {
    key: "shoulder_hip_angle_left",
    name: "肩髋角度",
    shortName: "肩髋角",
    unit: "度",
    description: "反映大臂相对躯干的抬高程度，角度越大表示大臂抬得越高，角度越小表示大臂越贴近身体。",
  },
  {
    key: "shoulder_arm_angle_left",
    name: "肩臂角度",
    shortName: "肩臂角",
    unit: "度",
    description: "衡量大臂相对肩线的外展程度，角度越小表示手臂越贴近身体中线，角度越大表示手臂越向外展开。",
  },
  {
    key: "shoulder_rotation_left",
    name: "肩部旋转角度",
    shortName: "肩转角",
    unit: "度",
    description: "反映击球过程中肩部的转动幅度，正值表示肩向前转动（随挥），负值表示肩向后转动（引拍）。",
  },
  {
    key: "hip_angle_left",
    name: "髋部角度",
    shortName: "髋角",
    unit: "度",
    description: "衡量髋关节的弯曲程度，角度越小表示身体越弯曲，角度越大表示身体越直立。",
  },
  {
    key: "hip_rotation_left",
    name: "髋部旋转角度",
    shortName: "髋转角",
    unit: "度",
    description: "反映击球过程中髋部的转动幅度，正值表示髋向前转动（蹬转发力），负值表示髋向后转动（引拍蓄力）。",
  },
  {
    key: "knee_angle_left",
    name: "膝盖角度",
    shortName: "膝角",
    unit: "度",
    description: "衡量膝盖弯曲程度的角度，角度越小表示下蹲越深，角度越大表示腿部越伸直。",
  },
  {
    key: "trunk_flexion_angle",
    name: "躯干前倾角度",
    shortName: "前倾角",
    unit: "度",
    description: "衡量上身前后倾斜程度，正值表示身体前倾，负值表示身体后仰，体现击球时的身体姿态。",
  },
  {
    key: "trunk_lateral_tilt_angle",
    name: "躯干侧倾角度",
    shortName: "躯干侧倾",
    unit: "度",
    description: "衡量上身左右倾斜程度，正值表示身体向右倾，负值表示身体向左倾，反映击球时的身体平衡状态。",
  },
  {
    key: "trunk_rotation_angle",
    name: "躯干扭转角度",
    shortName: "扭转角",
    unit: "度",
    description: "反映肩部相对髋部的扭转程度，负值表示引拍时肩髋分离蓄力，正值表示随挥时身体释放转动。",
  },
  {
    key: "center_of_gravity",
    name: "重心高度",
    shortName: "重心高",
    unit: "pu",
    description: "衡量全身重心相对腿长的高度比值，数值越小表示重心越低（下蹲越深），数值越大表示重心越高（身体越直立）。",
  },
];

const techTypeLabels: Record<string, string> = {
  left_backhand_backspin: "左手反手拉下旋球",
  left_backhand_chop: "左手反手削球",
  left_backhand_defense: "左手反手防守",
  left_backhand_drive: "左手反手攻球",
  left_backhand_push: "左手反手搓球",
  left_backhand_topspin: "左手反手拉上旋球",
  left_forehand_backspin: "左手正手拉下旋球",
  left_forehand_chop: "左手正手削球",
  left_forehand_defense: "左手正手防守",
  left_forehand_drive: "左手正手攻球",
  left_forehand_push: "左手正手搓球",
  left_forehand_topspin: "左手正手拉上旋球",
  right_backhand_backspin: "右手反手拉下旋球",
  right_backhand_chop: "右手反手削球",
  right_backhand_defense: "右手反手防守",
  right_backhand_drive: "右手反手攻球",
  right_backhand_push: "右手反手搓球",
  right_backhand_topspin: "右手反手拉上旋球",
  right_forehand_backspin: "右手正手拉下旋球",
  right_forehand_chop: "右手正手削球",
  right_forehand_defense: "右手正手防守",
  right_forehand_drive: "右手正手攻球",
  right_forehand_push: "右手正手搓球",
  right_forehand_topspin: "右手正手拉上旋球",
};

export function ResultPage() {
  const navigate = useNavigate();
  const { runId } = useParams();
  const [runStatus, setRunStatus] = useState<TeacherWebComparisonRunStatus | null>(null);
  const [llmAnalysis, setLlmAnalysis] = useState<TeacherWebLlmAnalysis | null>(null);
  const [llmStatus, setLlmStatus] = useState<LlmViewStatus>("idle");
  const [llmError, setLlmError] = useState("");
  const [llmPollingNotice, setLlmPollingNotice] = useState("");
  const [llmPollAttempt, setLlmPollAttempt] = useState(0);
  const [pageError, setPageError] = useState("");
  const [runPollingNotice, setRunPollingNotice] = useState("");
  const [runPollAttempt, setRunPollAttempt] = useState(0);
  const [modelErrors, setModelErrors] = useState<Partial<Record<ModelSlot, string>>>({});
  const [modelReady, setModelReady] = useState<Partial<Record<ModelSlot, boolean>>>({});
  const [modelUrls, setModelUrls] = useState<Partial<Record<ModelSlot, string>>>({});
  const [modelDurations, setModelDurations] = useState<Partial<Record<ModelSlot, number>>>({});
  const [modelHeights, setModelHeights] = useState<Partial<Record<ModelSlot, number>>>({});
  const [fullscreenSlot, setFullscreenSlot] = useState<ModelSlot | null>(null);
  const [showModelHelp, setShowModelHelp] = useState(false);
  const [selectedChartKey, setSelectedChartKey] = useState("");
  const [chartFrame, setChartFrame] = useState(0);
  const [isModelPlaying, setIsModelPlaying] = useState(false);
  const [modelPlaybackTime, setModelPlaybackTime] = useState(0);
  const [bindingStudentName, setBindingStudentName] = useState("");
  const [activeDetailTab, setActiveDetailTab] = useState<ResultDetailTab>("overview");
  const [assetUrls, setAssetUrls] = useState<Partial<Record<TeacherWebResultAssetName, string>>>({});
  const [keyFrameAssetError, setKeyFrameAssetError] = useState("");
  const [keyFrameAssetAttempt, setKeyFrameAssetAttempt] = useState(0);
  const [keyFrameAssetsLoading, setKeyFrameAssetsLoading] = useState(false);
  const [sourceVideoError, setSourceVideoError] = useState("");
  const [sourceVideoAttempt, setSourceVideoAttempt] = useState(0);
  const [sourceVideoLoading, setSourceVideoLoading] = useState(false);
  const [sourceVideoDuration, setSourceVideoDuration] = useState(0);
  const [showSourceVideo, setShowSourceVideo] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState("");
  const [webGlSupported] = useState(supportsWebGLRendering);
  const [modelViewCommand, setModelViewCommand] = useState<GlbViewCommand>({
    angle: 0,
    animated: false,
    orbitDistance: defaultOrbitDistance,
    revision: 0,
  });
  const modelObjectUrlCacheRef = useRef(new Map<string, string>());
  const modelDownloadPromiseCacheRef = useRef(new Map<string, Promise<string>>());
  const assetDownloadPromiseCacheRef = useRef(new Map<string, Promise<TeacherWebResultAssetUrl[]>>());
  const sourceVideoRef = useRef<HTMLVideoElement | null>(null);
  const isDisposedRef = useRef(false);
  const currentUserId = getStoredSession()?.user.uid;

  function clearStoredActiveRun(expectedRunId: string): void {
    const token = getLastTeacherToken();
    if (!token || !currentUserId) return;
    clearActiveAnalysisRun(token, currentUserId, expectedRunId);
  }

  useEffect(() => {
    const responseName = runStatus?.student_name?.trim();
    if (responseName) {
      setBindingStudentName(responseName);
      return;
    }
    if (!runStatus || currentUserId !== runStatus.student_user_id) return;

    const token = getLastTeacherToken();
    if (!token) return;
    const controller = new AbortController();
    getTeacherLinkBinding(token, controller.signal)
      .then((status) => setBindingStudentName(status.binding?.studentName?.trim() || ""))
      .catch(() => undefined);
    return () => controller.abort();
  }, [currentUserId, runStatus?.student_name, runStatus?.student_user_id]);

  useEffect(() => {
    if (!runId) {
      setPageError("缺少任务 ID");
      return;
    }

    const currentRunId = runId;
    const controller = new AbortController();
    setRunPollingNotice("");

    async function observeRun() {
      try {
        const outcome = await runAdaptivePolling({
          kind: "analysis",
          poll: async (signal) => {
            const status = await getTeacherWebComparisonRunStatus(currentRunId, signal);
            if (!signal.aborted) {
              setRunStatus(status);
              setPageError("");
              if (status.status === "comp completed" || status.status === "comp failed") {
                clearStoredActiveRun(currentRunId);
              }
            }
            return status;
          },
          evaluate: (status) => {
            if (status.status === "comp completed") {
              return { type: "completed", value: status };
            }
            if (status.status === "comp failed") {
              return {
                type: "failed",
                error: new Error(status.error_message || "AI教练分析失败，请重试"),
              };
            }
            return { type: "pending" };
          },
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;
        if (outcome.type === "observation-window-ended") {
          setRunPollingNotice("已暂停自动等待，分析仍可能在服务器继续。您可以继续等待或稍后重新打开本页。");
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        clearStoredActiveRun(currentRunId);
        setPageError(error instanceof Error ? error.message : "结果读取失败");
      }
    }

    void observeRun();

    return () => {
      controller.abort();
    };
  }, [runId, runPollAttempt]);

  useEffect(() => {
    if (!runId || runStatus?.status !== "comp completed") return;

    const currentRunId = runId;
    const controller = new AbortController();
    setLlmPollingNotice("");
    setLlmStatus("pending");
    setLlmError("");

    async function observeLlm() {
      try {
        const outcome = await runAdaptivePolling<TeacherWebLlmStatus, TeacherWebLlmAnalysis>({
          kind: "ai-report",
          poll: async (signal) => {
            const status = await getTeacherWebLlmStatus(currentRunId, signal);
            if (!signal.aborted) setLlmStatus(status.status);
            return status;
          },
          evaluate: (status) => {
            if (status.status === "completed") {
              const report = validLlmAnalysis(status.llm_analysis);
              if (!report) throw new Error("AI 报告数据格式异常");
              return { type: "completed", value: report };
            }
            if (status.status === "failed") {
              return { type: "failed", error: new Error("AI 报告生成失败") };
            }
            return { type: "pending" };
          },
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;
        if (outcome.type === "completed") {
          setLlmAnalysis(outcome.value);
          setLlmStatus("completed");
          setLlmError("");
          return;
        }

        setLlmAnalysis(null);
        if (outcome.type === "failed") {
          setLlmStatus("failed");
          setLlmError(outcome.error.message);
          return;
        }

        setLlmStatus("pending");
        setLlmPollingNotice("已暂停自动等待，AI 报告仍可能在服务器继续生成。");
      } catch (error) {
        if (controller.signal.aborted) return;
        setLlmStatus("error");
        setLlmError(error instanceof Error ? error.message : "AI 报告读取失败");
      }
    }

    void observeLlm();

    return () => {
      controller.abort();
    };
  }, [llmPollAttempt, runId, runStatus?.status]);

  const downloads = runStatus?.glb_downloads;
  const downloadSignature = `${downloads?.first?.objectKey ?? ""}|${downloads?.second?.objectKey ?? ""}`;

  useEffect(() => {
    setModelHeights({});
    setModelErrors({});
    setModelReady({});
    setModelUrls({});
  }, [downloadSignature]);

  useEffect(() => {
    if (!runId || runStatus?.status !== "comp completed" || !downloads || !webGlSupported) return;

    const controller = new AbortController();

    for (const slot of modelSlots) {
      const download = downloads[slot];
      const objectKey = download?.objectKey;
      if (!objectKey) continue;

      const cachedUrl = modelObjectUrlCacheRef.current.get(objectKey);
      if (cachedUrl) {
        setModelUrls((current) => ({ ...current, [slot]: cachedUrl }));
        continue;
      }

      let downloadPromise = modelDownloadPromiseCacheRef.current.get(objectKey);
      if (!downloadPromise) {
        downloadPromise = getTeacherWebGlbDownloadUrl(runId, slot, controller.signal).then((data) => {
          if (data.objectKey !== objectKey) {
            throw new Error("模型文件不匹配，请重试");
          }
          return loadGlbObjectUrlFromRemote(data.url, data.objectKey);
        });
        modelDownloadPromiseCacheRef.current.set(objectKey, downloadPromise);
      }

      setModelErrors((current) => ({ ...current, [slot]: "" }));

      downloadPromise
        .then((url) => {
          modelDownloadPromiseCacheRef.current.delete(objectKey);
          if (isDisposedRef.current) {
            URL.revokeObjectURL(url);
            return;
          }

          const existingUrl = modelObjectUrlCacheRef.current.get(objectKey);
          const nextUrl = existingUrl || url;
          if (existingUrl && existingUrl !== url) {
            URL.revokeObjectURL(url);
          } else {
            modelObjectUrlCacheRef.current.set(objectKey, url);
          }

          if (!controller.signal.aborted) {
            setModelUrls((current) => ({ ...current, [slot]: nextUrl }));
          }
        })
        .catch((error) => {
          modelDownloadPromiseCacheRef.current.delete(objectKey);
          if (!controller.signal.aborted) {
            setModelErrors((current) => ({
              ...current,
              [slot]: error instanceof Error ? error.message : "模型下载失败",
            }));
          }
        });
    }

    return () => controller.abort();
  }, [downloadSignature, downloads, runId, runStatus?.status, webGlSupported]);

  useEffect(() => {
    isDisposedRef.current = false;
    return () => {
      isDisposedRef.current = true;
      modelDownloadPromiseCacheRef.current.clear();
      assetDownloadPromiseCacheRef.current.clear();
      modelObjectUrlCacheRef.current.forEach((url) => URL.revokeObjectURL(url));
      modelObjectUrlCacheRef.current.clear();
    };
  }, []);

  const resultData = Array.isArray(runStatus?.comp_results?.data)
    ? runStatus.comp_results.data
    : [];
  const firstData = validCompData(resultData[0]);
  const secondData = validCompData(resultData[1]);
  const embeddedAnalysis = validLlmAnalysis(firstData?.qwen_analysis);
  const analysis = llmAnalysis ?? embeddedAnalysis;
  const displayedLlmStatus: LlmViewStatus = analysis
    ? "completed"
    : runStatus?.status === "comp failed"
      ? "failed"
      : llmStatus;
  const techType = typeof runStatus?.comp_results?.tech_type === "string"
    ? runStatus.comp_results.tech_type
    : undefined;
  const techLabel = techType ? techTypeLabels[techType] ?? techType : "AI教练分析";
  const maxModelDuration = Math.max(
    modelDurations.first ?? 0,
    showSourceVideo ? sourceVideoDuration : modelDurations.second ?? 0,
  );
  const isTeacherViewer = Boolean(currentUserId && runStatus?.teacher_user_id === currentUserId);
  const studentName = runStatus?.student_name?.trim() || bindingStudentName || "学生";
  const standardnessScore = useMemo(
    () => calculateComparisonStandardness(runStatus?.comp_results),
    [runStatus?.comp_results],
  );

  const primaryKeyFrames = runStatus?.comp_results?.key_frame_images?.primary;
  const keyFrameAssetNames = useMemo(() => {
    const names: TeacherWebResultAssetName[] = [];
    if (primaryKeyFrames?.start?.object_key) names.push("key_frame_start");
    if (primaryKeyFrames?.impact?.object_key) names.push("key_frame_impact");
    if (primaryKeyFrames?.end?.object_key) names.push("key_frame_end");
    return names;
  }, [
    primaryKeyFrames?.end?.object_key,
    primaryKeyFrames?.impact?.object_key,
    primaryKeyFrames?.start?.object_key,
  ]);
  const keyFrameAssetSignature = keyFrameAssetNames.join("|");

  const chartItems = useMemo(() => buildChartItems(firstData, secondData, preferredChartSide(techType)), [firstData, secondData, techType]);
  const selectedChart = chartItems.find((item) => item.definition.key === selectedChartKey) ?? chartItems[0] ?? null;
  const visualizationNodeName = selectedChart ? visualizationNodeNameForMetric(selectedChart.definition.key) : null;
  const sharedModelHeight = typeof modelHeights.first === "number" && typeof modelHeights.second === "number"
    ? Math.max(modelHeights.first, modelHeights.second)
    : undefined;
  const resultCompleted = runStatus?.status === "comp completed";
  const resultFailed = runStatus?.status === "comp failed";
  const unsupported3DMessage = "当前设备不支持 3D 模型，动作数据和 AI 报告仍可查看";
  const firstModelError = modelErrors.first
    || (resultFailed ? "本次分析失败，未生成学生 3D 模型" : !webGlSupported ? unsupported3DMessage : resultCompleted && !downloads?.first?.objectKey ? `${studentName}的 3D 模型文件缺失` : "");
  const secondModelError = modelErrors.second
    || (resultFailed ? "本次分析失败，未生成标准动作 3D 模型" : !webGlSupported ? unsupported3DMessage : resultCompleted && !downloads?.second?.objectKey ? "标准动作 3D 模型文件缺失" : "");
  const motionDataError = resultFailed
    ? "本次分析失败，未生成动作数据。"
    : resultCompleted && chartItems.length === 0
      ? "动作数据缺失或格式异常，3D 模型和 AI 报告仍可单独查看。"
      : "";

  useEffect(() => {
    if (chartItems.length === 0) {
      setSelectedChartKey("");
      return;
    }
    if (!chartItems.some((item) => item.definition.key === selectedChartKey)) {
      setSelectedChartKey(chartItems[0].definition.key);
      setChartFrame(0);
    }
  }, [chartItems, selectedChartKey]);

  useEffect(() => {
    if (
      !runId
      || runStatus?.status !== "comp completed"
      || activeDetailTab !== "insight"
      || keyFrameAssetNames.length === 0
    ) return;
    if (keyFrameAssetNames.every((asset) => assetUrls[asset])) return;

    let cancelled = false;
    const cacheKey = `${runId}:keyframes:${keyFrameAssetSignature}`;
    let request = assetDownloadPromiseCacheRef.current.get(cacheKey);
    if (!request) {
      request = getTeacherWebResultAssetUrls(runId, keyFrameAssetNames);
      assetDownloadPromiseCacheRef.current.set(cacheKey, request);
    }

    setKeyFrameAssetsLoading(true);
    setKeyFrameAssetError("");
    request
      .then((items) => {
        if (cancelled) return;
        setAssetUrls((current) => ({
          ...current,
          ...Object.fromEntries(items.map((item) => [item.asset, item.url])),
        }));
      })
      .catch((error) => {
        assetDownloadPromiseCacheRef.current.delete(cacheKey);
        if (!cancelled) {
          setKeyFrameAssetError(error instanceof Error ? error.message : "关键帧读取失败");
        }
      })
      .finally(() => {
        if (!cancelled) setKeyFrameAssetsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    activeDetailTab,
    assetUrls,
    keyFrameAssetAttempt,
    keyFrameAssetNames,
    keyFrameAssetSignature,
    runId,
    runStatus?.status,
  ]);

  useEffect(() => {
    if (
      !runId
      || !showSourceVideo
      || runStatus?.status !== "comp completed"
      || !runStatus.comp_results?.first_video_object_key
      || assetUrls.source_video
    ) return;

    let cancelled = false;
    const cacheKey = `${runId}:source_video`;
    let request = assetDownloadPromiseCacheRef.current.get(cacheKey);
    if (!request) {
      request = getTeacherWebResultAssetUrls(runId, ["source_video"]);
      assetDownloadPromiseCacheRef.current.set(cacheKey, request);
    }

    setSourceVideoLoading(true);
    setSourceVideoError("");
    request
      .then((items) => {
        if (cancelled) return;
        const source = items.find((item) => item.asset === "source_video");
        if (!source) throw new Error("原视频下载链接缺失");
        setAssetUrls((current) => ({ ...current, source_video: source.url }));
      })
      .catch((error) => {
        assetDownloadPromiseCacheRef.current.delete(cacheKey);
        if (!cancelled) {
          setSourceVideoError(error instanceof Error ? error.message : "原视频读取失败");
        }
      })
      .finally(() => {
        if (!cancelled) setSourceVideoLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [assetUrls.source_video, runId, runStatus?.comp_results?.first_video_object_key, runStatus?.status, showSourceVideo, sourceVideoAttempt]);

  useEffect(() => {
    const video = sourceVideoRef.current;
    if (!showSourceVideo || !video) return;
    if (Math.abs(video.currentTime - modelPlaybackTime) > 0.08) {
      video.currentTime = clamp(modelPlaybackTime, 0, video.duration || modelPlaybackTime);
    }
  }, [assetUrls.source_video, modelPlaybackTime, showSourceVideo]);

  useEffect(() => {
    const video = sourceVideoRef.current;
    if (!showSourceVideo || !video) return;
    if (isModelPlaying) {
      void video.play().catch(() => setIsModelPlaying(false));
    } else {
      video.pause();
    }
  }, [assetUrls.source_video, isModelPlaying, showSourceVideo]);

  if (!runId) {
    return <ResultErrorCard message="缺少任务 ID" />;
  }

  function handleLogout() {
    setLogoutError("");
    setShowLogoutConfirm(true);
  }

  function handleLogoutCancel() {
    if (isLoggingOut) return;
    setLogoutError("");
    setShowLogoutConfirm(false);
  }

  async function handleLogoutConfirm() {
    setIsLoggingOut(true);
    setLogoutError("");

    try {
      await logoutCurrentSession();
      const token = getLastTeacherToken();
      if (token && currentUserId) {
        clearActiveAnalysisRun(token, currentUserId, runId);
      }
      setShowLogoutConfirm(false);
      navigate("/compat", { replace: true });
    } catch (error) {
      setLogoutError(logoutFailureMessage(error));
    } finally {
      setIsLoggingOut(false);
    }
  }

  function handleReselectVideo() {
    const token = getLastTeacherToken();
    if (token) {
      if (currentUserId) clearActiveAnalysisRun(token, currentUserId, runId);
      navigate(`/t/${token}`);
      return;
    }
    navigate(-1);
  }

  function handleBackToDashboard() {
    navigate("/teacher/dashboard");
  }

  function handleModelTimeUpdate(time: number) {
    if (!isModelPlaying) return;
    setModelPlaybackTime(time);
    if (selectedChart) {
      setChartFrame(frameFromProgress(time, maxModelDuration, selectedChart.maxFrameCount));
    }
  }

  function handleChartFrameChange(frame: number) {
    if (!selectedChart) return;
    const nextFrame = clamp(Math.round(frame), 0, Math.max(0, selectedChart.maxFrameCount - 1));
    setChartFrame(nextFrame);
    if (maxModelDuration > 0 && selectedChart.maxFrameCount > 1) {
      setModelPlaybackTime((nextFrame / (selectedChart.maxFrameCount - 1)) * maxModelDuration);
    }
  }

  function handleViewingAngleChange(angle: number, animated: boolean) {
    setModelViewCommand((current) => ({
      ...current,
      angle,
      animated,
      revision: current.revision + 1,
    }));
  }

  function handleZoomChange(change: number) {
    setModelViewCommand((current) => ({
      ...current,
      animated: true,
      orbitDistance: clamp(
        current.orbitDistance + change,
        minimumOrbitDistance,
        maximumOrbitDistance,
      ),
      revision: current.revision + 1,
    }));
  }

  function handleZoomReset() {
    setModelViewCommand((current) => ({
      ...current,
      animated: true,
      orbitDistance: defaultOrbitDistance,
      revision: current.revision + 1,
    }));
  }

  const modelPlaybackDisabled = !webGlSupported || (!modelReady.first
    && !(showSourceVideo ? assetUrls.source_video : modelReady.second));
  const modelControlsDisabled = !webGlSupported || !modelReady.first || !modelReady.second;

  return (
    <section className="result-page">
      <header className="result-mobile-header">
        <div className="upload-wizard-brand-row">
          <div className="student-login-brand" aria-label="迹时运动">
            <img src="/pingpi-logo.png?v=cff2de3ac9d0" alt="" aria-hidden="true" />
            <span>迹时运动</span>
          </div>
          <div className="result-header-actions">
            {isTeacherViewer ? (
              <button className="result-text-button" type="button" onClick={handleBackToDashboard}>
                返回教师看板
              </button>
            ) : (
              <button className="result-text-button" type="button" onClick={handleReselectVideo}>
                重新选择视频
              </button>
            )}
            <button className="upload-logout-button" type="button" aria-label="退出登录" title="退出登录" onClick={handleLogout}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 6.75V5.5A2.5 2.5 0 0 1 11.5 3h5A2.5 2.5 0 0 1 19 5.5v13a2.5 2.5 0 0 1-2.5 2.5h-5A2.5 2.5 0 0 1 9 18.5v-1.25" />
                <path d="M4 12h10" />
                <path d="m11 8 4 4-4 4" />
              </svg>
            </button>
          </div>
        </div>

        <div className="result-title-row">
          <div>
            <span className={`status-pill ${statusTone(runStatus?.status, pageError)}`}>{statusText(runStatus?.status, pageError)}</span>
            <h2>{techLabel}</h2>
          </div>
          <button className="round-help-button" type="button" aria-label="查看模型操作说明" onClick={() => setShowModelHelp(true)}>
            ?
          </button>
        </div>
      </header>

      {pageError ? <ResultErrorCard message={pageError} /> : null}
      {runPollingNotice ? (
        <article className="result-card result-polling-notice" role="status">
          <div>
            <strong>分析仍可能在服务器继续</strong>
            <p>{runPollingNotice}</p>
          </div>
          <button
            className="ghost-button"
            type="button"
            onClick={() => setRunPollAttempt((value) => value + 1)}
          >
            继续等待
          </button>
        </article>
      ) : null}

      <article className="result-card result-model-section">
        <div className="dual-model-grid">
          <ModelPanel
            bodyCenter={firstData?.body_center}
            directions={firstData?.directions}
            error={firstModelError}
            glbUrl={modelUrls.first ?? null}
            isPlaying={isModelPlaying}
            isReady={Boolean(modelReady.first)}
            label={`${studentName}动作`}
            playbackTime={modelPlaybackTime}
            slot="first"
            sharedModelHeight={sharedModelHeight}
            viewCommand={modelViewCommand}
            visualizationNodeName={visualizationNodeName}
            onDurationChange={(duration) => setModelDurations((current) => ({ ...current, first: duration }))}
            onError={(message) => setModelErrors((current) => ({ ...current, first: message }))}
            onFullscreen={() => setFullscreenSlot("first")}
            onModelHeightChange={(height) => setModelHeights((current) => ({ ...current, first: height }))}
            onReady={() => setModelReady((current) => ({ ...current, first: true }))}
            onTimeUpdate={handleModelTimeUpdate}
          />
          {showSourceVideo ? (
            <SourceVideoPanel
              error={sourceVideoError}
              isLoading={sourceVideoLoading}
              label="原视频"
              videoRef={sourceVideoRef}
              videoUrl={assetUrls.source_video ?? null}
              onDurationChange={setSourceVideoDuration}
              onError={() => setSourceVideoError("原视频加载失败，请稍后重试")}
              onFullscreen={() => {
                setIsModelPlaying(false);
                setFullscreenSlot("second");
              }}
              onTimeUpdate={(time) => {
                if (!isModelPlaying) return;
                setModelPlaybackTime(time);
                if (selectedChart) {
                  setChartFrame(frameFromProgress(time, maxModelDuration, selectedChart.maxFrameCount));
                }
              }}
              onRetry={() => {
                assetDownloadPromiseCacheRef.current.delete(`${runId}:source_video`);
                setAssetUrls((current) => {
                  const next = { ...current };
                  delete next.source_video;
                  return next;
                });
                setSourceVideoError("");
                setSourceVideoAttempt((value) => value + 1);
              }}
              onToggle={() => {
                setIsModelPlaying(false);
                setShowSourceVideo(false);
              }}
            />
          ) : (
            <ModelPanel
              bodyCenter={secondData?.body_center}
              directions={secondData?.directions}
              error={secondModelError}
              glbUrl={modelUrls.second ?? null}
              headerAction={runStatus?.comp_results?.first_video_object_key ? (
                <button
                  className="model-source-toggle"
                  type="button"
                  onClick={() => {
                    setIsModelPlaying(false);
                    setShowSourceVideo(true);
                  }}
                >
                  原视频
                </button>
              ) : null}
              isPlaying={isModelPlaying}
              isReady={Boolean(modelReady.second)}
              label="标准动作"
              playbackTime={modelPlaybackTime}
              slot="second"
              sharedModelHeight={sharedModelHeight}
              viewCommand={modelViewCommand}
              visualizationNodeName={visualizationNodeName}
              onDurationChange={(duration) => setModelDurations((current) => ({ ...current, second: duration }))}
              onError={(message) => setModelErrors((current) => ({ ...current, second: message }))}
              onFullscreen={() => setFullscreenSlot("second")}
              onModelHeightChange={(height) => setModelHeights((current) => ({ ...current, second: height }))}
              onReady={() => setModelReady((current) => ({ ...current, second: true }))}
              onTimeUpdate={undefined}
            />
          )}
        </div>
        <ModelPlaybackControl
          disabled={modelPlaybackDisabled}
          duration={maxModelDuration}
          isPlaying={isModelPlaying}
          time={modelPlaybackTime}
          onSeek={(time) => {
            setModelPlaybackTime(time);
            if (selectedChart) {
              setChartFrame(frameFromProgress(time, maxModelDuration, selectedChart.maxFrameCount));
            }
          }}
          onToggle={() => setIsModelPlaying((value) => !value)}
        />
      </article>

      <section className="result-detail-tabs" aria-label="分析详情">
        <div className="result-detail-tab-list" role="tablist" aria-label="分析详情分类">
          <button
            id="result-detail-overview-tab"
            className={activeDetailTab === "overview" ? "active" : ""}
            type="button"
            role="tab"
            aria-controls="result-detail-tab-panel"
            aria-selected={activeDetailTab === "overview"}
            onClick={() => setActiveDetailTab("overview")}
          >
            总览
          </button>
          <button
            id="result-detail-data-tab"
            className={activeDetailTab === "data" ? "active" : ""}
            type="button"
            role="tab"
            aria-controls="result-detail-tab-panel"
            aria-selected={activeDetailTab === "data"}
            onClick={() => setActiveDetailTab("data")}
          >
            数据
          </button>
          <button
            id="result-detail-insight-tab"
            className={activeDetailTab === "insight" ? "active" : ""}
            type="button"
            role="tab"
            aria-controls="result-detail-tab-panel"
            aria-selected={activeDetailTab === "insight"}
            onClick={() => setActiveDetailTab("insight")}
          >
            洞察
          </button>
          <button
            id="result-detail-control-tab"
            className={activeDetailTab === "control" ? "active" : ""}
            type="button"
            role="tab"
            aria-controls="result-detail-tab-panel"
            aria-selected={activeDetailTab === "control"}
            onClick={() => setActiveDetailTab("control")}
          >
            控制
          </button>
          <span className={`result-detail-tab-indicator ${activeDetailTab}`} aria-hidden="true" />
        </div>

        <div
          id="result-detail-tab-panel"
          className="result-detail-tab-panel"
          role="tabpanel"
          aria-labelledby={`result-detail-${activeDetailTab}-tab`}
        >
          {activeDetailTab === "overview" ? (
            <ComparisonOverview
              llmStatus={displayedLlmStatus}
              score={standardnessScore}
              summary={analysis?.summary}
            />
          ) : activeDetailTab === "data" ? (
            <article className="result-card result-data-card">
              <MotionChart
                emptyMessage={motionDataError || "暂无动作数据。"}
                item={selectedChart}
                isPlaying={isModelPlaying}
                primaryName={studentName}
                selectedFrame={chartFrame}
                playbackDisabled={modelPlaybackDisabled}
                onChartChange={(key) => {
                  setSelectedChartKey(key);
                  setChartFrame(0);
                }}
                onFrameChange={handleChartFrameChange}
                onPlaybackToggle={() => setIsModelPlaying((value) => !value)}
                options={chartItems}
              />
            </article>
          ) : activeDetailTab === "insight" ? (
            <article className="result-card result-report-card">
              <div className="result-card-header compact">
                <div>
                  <h3>AI报告</h3>
                </div>
                <span className={`status-pill ${analysis ? "success" : displayedLlmStatus === "failed" || displayedLlmStatus === "error" ? "error" : "running"}`}>
                  {analysis ? "已生成" : displayedLlmStatus === "failed" ? "生成失败" : displayedLlmStatus === "error" ? "读取失败" : "生成中"}
                </span>
              </div>
              <LlmReport
                analysis={analysis}
                assetUrls={assetUrls}
                keyFrameError={keyFrameAssetError}
                keyFramesLoading={keyFrameAssetsLoading}
                llmError={llmError}
                llmPollingNotice={llmPollingNotice}
                llmStatus={displayedLlmStatus}
                onRetryLlm={() => {
                  setLlmAnalysis(null);
                  setLlmError("");
                  setLlmPollingNotice("");
                  setLlmStatus("pending");
                  setLlmPollAttempt((value) => value + 1);
                }}
                onRetryKeyFrames={() => {
                  setAssetUrls((current) => {
                    const next = { ...current };
                    delete next.key_frame_start;
                    delete next.key_frame_impact;
                    delete next.key_frame_end;
                    return next;
                  });
                  setKeyFrameAssetAttempt((value) => value + 1);
                }}
              />
            </article>
          ) : (
            <ComparisonViewControls
              angle={modelViewCommand.angle}
              disabled={modelControlsDisabled}
              orbitDistance={modelViewCommand.orbitDistance}
              onAngleChange={handleViewingAngleChange}
              onReset={handleZoomReset}
              onZoomIn={() => handleZoomChange(-orbitDistanceStep)}
              onZoomOut={() => handleZoomChange(orbitDistanceStep)}
            />
          )}
        </div>
      </section>

      {showModelHelp ? (
        <InfoDialog title="3D模型操作" onClose={() => setShowModelHelp(false)}>
          <p>单指拖动可以旋转模型。</p>
          <p>双指可以放大、缩小或平移视角。</p>
        </InfoDialog>
      ) : null}

      {fullscreenSlot === "second" && showSourceVideo ? (
        <FullscreenVideo
          isPlaying={isModelPlaying}
          label="原视频"
          playbackTime={modelPlaybackTime}
          videoUrl={assetUrls.source_video ?? null}
          onClose={() => setFullscreenSlot(null)}
          onTimeUpdate={(time) => {
            setModelPlaybackTime(time);
          }}
        />
      ) : fullscreenSlot ? (
        <FullscreenModel
          bodyCenter={fullscreenSlot === "first" ? firstData?.body_center : secondData?.body_center}
          directions={fullscreenSlot === "first" ? firstData?.directions : secondData?.directions}
          glbUrl={modelUrls[fullscreenSlot] ?? null}
          isPlaying={isModelPlaying}
          label={fullscreenSlot === "first" ? `${studentName}动作` : "标准动作"}
          playbackTime={modelPlaybackTime}
          sharedModelHeight={sharedModelHeight}
          viewCommand={modelViewCommand}
          visualizationNodeName={visualizationNodeName}
          onClose={() => setFullscreenSlot(null)}
          onDurationChange={(duration) => setModelDurations((current) => ({ ...current, [fullscreenSlot]: duration }))}
          onError={(message) => setModelErrors((current) => ({ ...current, [fullscreenSlot]: message }))}
          onReady={() => setModelReady((current) => ({ ...current, [fullscreenSlot]: true }))}
          onTimeUpdate={handleModelTimeUpdate}
        />
      ) : null}

      {showLogoutConfirm ? (
        <LogoutConfirmDialog
          error={logoutError}
          isSubmitting={isLoggingOut}
          onCancel={handleLogoutCancel}
          onConfirm={handleLogoutConfirm}
        />
      ) : null}
    </section>
  );
}

function ModelPanel({
  bodyCenter,
  directions,
  error,
  glbUrl,
  headerAction,
  isPlaying,
  isReady,
  label,
  playbackTime,
  onDurationChange,
  onError,
  onFullscreen,
  onModelHeightChange,
  onReady,
  onTimeUpdate,
  sharedModelHeight,
  viewCommand,
  visualizationNodeName,
}: {
  bodyCenter?: number[];
  directions?: Record<string, number[]>;
  error?: string;
  glbUrl: string | null;
  headerAction?: ReactNode;
  isPlaying: boolean;
  isReady: boolean;
  label: string;
  playbackTime: number;
  slot: ModelSlot;
  onDurationChange: (duration: number) => void;
  onError: (message: string) => void;
  onFullscreen: () => void;
  onModelHeightChange: (height: number) => void;
  onReady: () => void;
  onTimeUpdate?: (time: number) => void;
  sharedModelHeight?: number;
  viewCommand: GlbViewCommand;
  visualizationNodeName: string | null;
}) {
  return (
    <section className="model-panel">
      <div className="model-panel-title">
        <div className="model-panel-heading">
          <strong>{label}</strong>
          {headerAction}
        </div>
        {glbUrl && isReady && !error ? <ModelFullscreenButton label={label} onClick={onFullscreen} /> : null}
      </div>
      <div className="result-model-stage compact">
        <GlbPreview
          bodyCenter={bodyCenter}
          directions={directions}
          emptyLabel={null}
          glbUrl={glbUrl}
          isPlaying={isPlaying}
          playbackTime={playbackTime}
          onDurationChange={onDurationChange}
          onError={onError}
          onModelHeightChange={onModelHeightChange}
          onReady={onReady}
          onTimeUpdate={onTimeUpdate}
          sharedModelHeight={sharedModelHeight}
          viewCommand={viewCommand}
          visualizationNodeName={visualizationNodeName}
        />
        {!isReady || error ? (
          <div className={`result-asset-state ${error ? "error" : ""}`} role="status">
            {!error ? <i className="loading" aria-hidden="true" /> : null}
            <span>{error || "正在读取 3D 模型"}</span>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function SourceVideoPanel({
  error,
  isLoading,
  label,
  onDurationChange,
  onError,
  onFullscreen,
  onRetry,
  onTimeUpdate,
  onToggle,
  videoRef,
  videoUrl,
}: {
  error: string;
  isLoading: boolean;
  label: string;
  onDurationChange: (duration: number) => void;
  onError: () => void;
  onFullscreen: () => void;
  onRetry: () => void;
  onTimeUpdate: (time: number) => void;
  onToggle: () => void;
  videoRef: RefObject<HTMLVideoElement | null>;
  videoUrl: string | null;
}) {
  return (
    <section className="model-panel">
      <div className="model-panel-title">
        <div className="model-panel-heading">
          <strong>{label}</strong>
          <button className="model-source-toggle" type="button" onClick={onToggle}>标准动作</button>
        </div>
        {videoUrl && !error ? <ModelFullscreenButton label={label} onClick={onFullscreen} /> : null}
      </div>
      <div className="result-model-stage compact source-video-stage">
        {videoUrl && !error ? (
          <video
            ref={videoRef}
            aria-label={label}
            playsInline
            preload="metadata"
            src={videoUrl}
            onError={onError}
            onDurationChange={(event) => onDurationChange(event.currentTarget.duration || 0)}
            onLoadedMetadata={(event) => onDurationChange(event.currentTarget.duration || 0)}
            onTimeUpdate={(event) => onTimeUpdate(event.currentTarget.currentTime)}
          />
        ) : (
          <div className="result-asset-state">
            {isLoading ? <i className="loading" aria-hidden="true" /> : null}
            <span>{isLoading ? "正在读取原视频" : error || "原视频暂不可用"}</span>
            {!isLoading && error ? <button type="button" onClick={onRetry}>重新读取</button> : null}
          </div>
        )}
      </div>
    </section>
  );
}

function ModelFullscreenButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button className="model-fullscreen-button" type="button" aria-label={`放大${label}`} onClick={onClick}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M8 3H3v5" />
        <path d="M3 3l7 7" />
        <path d="M16 3h5v5" />
        <path d="M21 3l-7 7" />
        <path d="M8 21H3v-5" />
        <path d="M3 21l7-7" />
        <path d="M16 21h5v-5" />
        <path d="M21 21l-7-7" />
      </svg>
    </button>
  );
}

function FullscreenModel({
  bodyCenter,
  directions,
  glbUrl,
  isPlaying,
  label,
  playbackTime,
  sharedModelHeight,
  onClose,
  onDurationChange,
  onError,
  onReady,
  onTimeUpdate,
  viewCommand,
  visualizationNodeName,
}: {
  bodyCenter?: number[];
  directions?: Record<string, number[]>;
  glbUrl: string | null;
  isPlaying: boolean;
  label: string;
  playbackTime: number;
  sharedModelHeight?: number;
  onClose: () => void;
  onDurationChange: (duration: number) => void;
  onError: (message: string) => void;
  onReady: () => void;
  onTimeUpdate?: (time: number) => void;
  viewCommand: GlbViewCommand;
  visualizationNodeName: string | null;
}) {
  return (
    <div className="fullscreen-model-backdrop">
      <div className="fullscreen-model">
        <header>
          <strong>{label}</strong>
          <button type="button" onClick={onClose}>关闭</button>
        </header>
        <GlbPreview
          bodyCenter={bodyCenter}
          directions={directions}
          emptyLabel={null}
          glbUrl={glbUrl}
          isPlaying={isPlaying}
          playbackTime={playbackTime}
          onDurationChange={onDurationChange}
          onError={onError}
          onReady={onReady}
          onTimeUpdate={onTimeUpdate}
          sharedModelHeight={sharedModelHeight}
          viewCommand={viewCommand}
          visualizationNodeName={visualizationNodeName}
        />
      </div>
    </div>
  );
}

function FullscreenVideo({
  isPlaying,
  label,
  onClose,
  onTimeUpdate,
  playbackTime,
  videoUrl,
}: {
  isPlaying: boolean;
  label: string;
  onClose: () => void;
  onTimeUpdate: (time: number) => void;
  playbackTime: number;
  videoUrl: string | null;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (Math.abs(video.currentTime - playbackTime) > 0.08) {
      video.currentTime = clamp(playbackTime, 0, video.duration || playbackTime);
    }
  }, [playbackTime]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [isPlaying, videoUrl]);

  return (
    <div className="fullscreen-model-backdrop">
      <div className="fullscreen-model fullscreen-video">
        <header>
          <strong>{label}</strong>
          <button type="button" onClick={onClose}>关闭</button>
        </header>
        {videoUrl ? (
          <video
            ref={videoRef}
            controls
            playsInline
            preload="metadata"
            src={videoUrl}
            onTimeUpdate={(event) => onTimeUpdate(event.currentTarget.currentTime)}
          />
        ) : <div className="result-asset-state"><span>原视频暂不可用</span></div>}
      </div>
    </div>
  );
}

function ModelPlaybackControl({
  disabled,
  duration,
  isPlaying,
  time,
  onSeek,
  onToggle,
}: {
  disabled: boolean;
  duration: number;
  isPlaying: boolean;
  time: number;
  onSeek: (time: number) => void;
  onToggle: () => void;
}) {
  return (
    <div className="model-playback-control">
      <PlaybackToggleButton disabled={disabled} isPlaying={isPlaying} onToggle={onToggle} />
      <input
        aria-label="模型播放进度"
        disabled={disabled || duration <= 0}
        max={duration || 1}
        min={0}
        step={0.01}
        type="range"
        value={clamp(time, 0, duration || 1)}
        onChange={(event) => onSeek(Number(event.target.value))}
      />
      <span>{formatShortTime(time)}</span>
    </div>
  );
}

function MotionChart({
  emptyMessage,
  item,
  isPlaying,
  onChartChange,
  onFrameChange,
  onPlaybackToggle,
  options,
  playbackDisabled,
  primaryName,
  selectedFrame,
}: {
  emptyMessage: string;
  item: ChartItem | null;
  isPlaying: boolean;
  onChartChange: (key: string) => void;
  onFrameChange: (frame: number) => void;
  onPlaybackToggle: () => void;
  options: ChartItem[];
  playbackDisabled: boolean;
  primaryName: string;
  selectedFrame: number;
}) {
  const [showMetricInfo, setShowMetricInfo] = useState(false);

  if (!item) {
    return (
      <div className="result-resource-state error" role="status">
        <strong>动作数据暂不可用</strong>
        <p>{emptyMessage}</p>
      </div>
    );
  }
  const inspection = chartInspection(item, selectedFrame);
  const missingChartResources = [
    !item.seriesFirst ? `${primaryName}的此项曲线缺失` : "",
    !item.seriesSecond ? "标准动作的此项曲线缺失" : "",
    item.seriesSecond && (!item.seriesSecond.mean || !item.seriesSecond.standardDeviation)
      ? "此项标准区间缺失"
      : "",
  ].filter(Boolean);

  return (
    <div className="motion-chart-card">
      <div className="chart-metric-header">
        <div className="chart-metric-select">
          <select value={item.definition.key} onChange={(event) => onChartChange(event.target.value)} aria-label="选择数据指标">
            {options.map((option) => (
              <option key={option.definition.key} value={option.definition.key}>
                {option.definition.name}
              </option>
            ))}
          </select>
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="m4 6 4 4 4-4" />
          </svg>
        </div>
        <button
          aria-label={`查看${item.definition.name}指标说明`}
          className="chart-metric-info-button"
          type="button"
          onClick={() => setShowMetricInfo(true)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 10.5v6" />
            <path d="M12 7.5h.01" />
          </svg>
        </button>
      </div>
      {missingChartResources.length ? (
        <div className="result-resource-inline-notice" role="status">
          {missingChartResources.join("；")}。其余可用数据仍会正常显示。
        </div>
      ) : null}
      <div className="chart-legend">
        <div className="chart-legend-row">
          <span><i className="student" />{primaryName}</span>
          <strong>{formatMetricValue(inspection.primaryValue, item.definition)}</strong>
        </div>
        <div className="chart-legend-row">
          <span><i className="standard" />标准动作</span>
          <strong>{formatMetricValue(inspection.referenceValue, item.definition)}</strong>
        </div>
        {inspection.standardRange ? (
          <div className="chart-legend-row">
            <span><i className="standard-range" />标准区间</span>
            <strong>{formatMetricRange(inspection.standardRange, item.definition)}</strong>
          </div>
        ) : null}
      </div>
      <DualLineChart item={item} selectedFrame={selectedFrame} />
      <div className="chart-frame-slider">
        <PlaybackToggleButton
          disabled={playbackDisabled}
          isPlaying={isPlaying}
          onToggle={onPlaybackToggle}
        />
        <input
          aria-label="动作数据帧位置"
          max={Math.max(0, item.maxFrameCount - 1)}
          min={0}
          step={1}
          type="range"
          value={clamp(selectedFrame, 0, Math.max(0, item.maxFrameCount - 1))}
          onChange={(event) => onFrameChange(Number(event.target.value))}
        />
        <span>{selectedFrame}/{Math.max(0, item.maxFrameCount - 1)} 帧</span>
      </div>
      {showMetricInfo ? (
        <MetricExplanationDialog
          description={item.definition.description}
          title={item.definition.name}
          onClose={() => setShowMetricInfo(false)}
        />
      ) : null}
    </div>
  );
}

function DualLineChart({ item, selectedFrame }: { item: ChartItem; selectedFrame: number }) {
  const width = 720;
  const height = 286;
  const padding = { bottom: 38, left: 52, right: 20, top: 34 };
  const primaryValues = item.seriesFirst?.original ?? [];
  const referenceValues = item.seriesSecond?.original ?? [];
  const rangeValues = standardRangeValues(item.seriesSecond);
  const values = [...primaryValues, ...referenceValues, ...rangeValues.lower, ...rangeValues.upper];
  const minValue = values.length ? Math.min(...values) : 0;
  const maxValue = values.length ? Math.max(...values) : 1;
  const range = maxValue - minValue || 1;
  const yMin = minValue - range * 0.2;
  const yMax = maxValue + range * 0.2;
  const yRange = yMax - yMin || 1;
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  function coordinates(series: number[]) {
    return series.map((value, index): [number, number] => {
      const x = padding.left + (index / Math.max(1, series.length - 1)) * plotWidth;
      const y = padding.top + (1 - (value - yMin) / yRange) * plotHeight;
      return [x, y];
    });
  }

  const currentX = padding.left + (clamp(selectedFrame, 0, item.maxFrameCount - 1) / Math.max(1, item.maxFrameCount - 1)) * plotWidth;
  const yTicks = [0, 0.5, 1].map((ratio) => yMin + (1 - ratio) * yRange);
  const xTicks = [0, 0.25, 0.5, 0.75, 1];
  const primaryPoints = coordinates(primaryValues);
  const referencePoints = coordinates(referenceValues);
  const rangeUpperPoints = coordinates(rangeValues.upper);
  const rangeLowerPoints = coordinates(rangeValues.lower);
  const currentProgress = clamp(selectedFrame, 0, item.maxFrameCount - 1) / Math.max(1, item.maxFrameCount - 1);
  const primaryCurrent = sampledPoint(primaryPoints, currentProgress);
  const referenceCurrent = sampledPoint(referencePoints, currentProgress);

  return (
    <svg className="motion-chart-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${item.definition.name} 对比曲线`}>
      <rect className="chart-plot-bg" x={padding.left} y={padding.top} width={plotWidth} height={plotHeight} />
      {yTicks.map((_, index) => {
        const y = padding.top + index * (plotHeight / 2);
        return <line className="chart-grid-line" key={index} x1={padding.left} x2={width - padding.right} y1={y} y2={y} />;
      })}
      {xTicks.map((progress) => {
        const x = padding.left + progress * plotWidth;
        return <line className="chart-grid-line" key={progress} x1={x} x2={x} y1={padding.top} y2={height - padding.bottom} />;
      })}
      {rangeUpperPoints.length > 1 && rangeLowerPoints.length > 1 ? (
        <path className="chart-standard-range" d={areaPath(rangeUpperPoints, rangeLowerPoints)} />
      ) : null}
      {primaryPoints.length > 1 ? <path className="chart-line student" d={smoothPath(primaryPoints)} fill="none" /> : null}
      {referencePoints.length > 1 ? <path className="chart-line standard" d={smoothPath(referencePoints)} fill="none" /> : null}
      <KeyFrameRules
        frames={item.keyFramesFirst}
        kind="student"
        plotBottom={height - padding.bottom}
        plotTop={padding.top}
        plotWidth={plotWidth}
        seriesLength={primaryValues.length}
        startX={padding.left}
      />
      <KeyFrameRules
        frames={item.keyFramesSecond}
        kind="standard"
        plotBottom={height - padding.bottom}
        plotTop={padding.top}
        plotWidth={plotWidth}
        seriesLength={referenceValues.length}
        startX={padding.left}
      />
      <line className="chart-current-line" x1={currentX} x2={currentX} y1={padding.top} y2={height - padding.bottom} />
      {primaryCurrent ? <circle className="chart-current-point student" cx={primaryCurrent[0]} cy={primaryCurrent[1]} r="4" /> : null}
      {referenceCurrent ? <circle className="chart-current-point standard" cx={referenceCurrent[0]} cy={referenceCurrent[1]} r="4" /> : null}
      <rect className="chart-plot-border" x={padding.left} y={padding.top} width={plotWidth} height={plotHeight} />
      {yTicks.map((value, index) => {
        const y = padding.top + index * (plotHeight / 2);
        return <text className="chart-tick" key={value} x={padding.left - 9} y={y + 4} textAnchor="end">{formatAxisValue(value, item.definition.key, yRange)}</text>;
      })}
      {xTicks.map((progress) => (
        <text className="chart-tick" key={progress} x={padding.left + progress * plotWidth} y={height - 13} textAnchor="middle">
          {formatProgress(progress)}
        </text>
      ))}
      <text className="chart-axis-label" x={12} y={height - 13}>{item.definition.unit}</text>
      <text className="chart-axis-label" x={width - 9} y={height - 13}>s</text>
    </svg>
  );
}

function PlaybackToggleButton({
  disabled,
  isPlaying,
  onToggle,
}: {
  disabled: boolean;
  isPlaying: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      className="playback-toggle-button"
      disabled={disabled}
      type="button"
      onClick={onToggle}
      aria-label={isPlaying ? "暂停模型和动作数据" : "播放模型和动作数据"}
    >
      {isPlaying ? (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 5v14" />
          <path d="M16 5v14" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 5.5v13l10-6.5-10-6.5Z" />
        </svg>
      )}
    </button>
  );
}

function KeyFrameRules({
  frames,
  kind,
  plotBottom,
  plotTop,
  plotWidth,
  seriesLength,
  startX,
}: {
  frames: number[];
  kind: "student" | "standard";
  plotBottom: number;
  plotTop: number;
  plotWidth: number;
  seriesLength: number;
  startX: number;
}) {
  const labels = ["启动", "击球", "随挥"];
  if (seriesLength < 2) return null;

  return frames.slice(0, labels.length).map((frame, index) => {
    const progress = clamp(frame, 0, seriesLength - 1) / (seriesLength - 1);
    const x = startX + progress * plotWidth;
    const labelY = kind === "student" ? plotTop + 13 + (index % 2) * 18 : plotBottom - 12 - (index % 2) * 18;
    const labelWidth = 34;
    const labelX = clamp(x, startX + labelWidth / 2 + 2, startX + plotWidth - labelWidth / 2 - 2);

    return (
      <g className={`chart-keyframe ${kind}`} key={`${kind}-${index}-${frame}`}>
        <line className="chart-keyframe-line" x1={x} x2={x} y1={plotTop} y2={plotBottom} />
        <rect className="chart-keyframe-label-bg" height="17" rx="4" width={labelWidth} x={labelX - labelWidth / 2} y={labelY - 11} />
        <text className="chart-keyframe-label" textAnchor="middle" x={labelX} y={labelY}>{labels[index]}</text>
      </g>
    );
  });
}

function LlmReport({
  analysis,
  assetUrls,
  keyFrameError,
  keyFramesLoading,
  llmError,
  llmPollingNotice,
  llmStatus,
  onRetryLlm,
  onRetryKeyFrames,
}: {
  analysis: TeacherWebLlmAnalysis | null;
  assetUrls: Partial<Record<TeacherWebResultAssetName, string>>;
  keyFrameError: string;
  keyFramesLoading: boolean;
  llmError: string;
  llmPollingNotice: string;
  llmStatus: LlmViewStatus;
  onRetryLlm: () => void;
  onRetryKeyFrames: () => void;
}) {
  if (!analysis) {
    const failed = llmStatus === "failed";
    const readError = llmStatus === "error";
    return (
      <div className={`report-empty ${failed || readError ? "error" : ""}`} role="status">
        <strong>{failed ? "AI 报告生成失败" : readError ? "AI 报告读取失败" : "报告生成中"}</strong>
        <p>
          {failed
            ? "本次未生成报告，3D 模型和动作数据不受影响。"
            : readError
              ? llmError || "报告暂未取到，请稍后重试。"
              : llmPollingNotice || "模型和数据会先展示，报告完成后本页会自动更新。"}
        </p>
        {readError || llmPollingNotice ? (
          <button className="ghost-button" type="button" onClick={onRetryLlm}>
            {llmPollingNotice ? "继续等待报告" : "重新读取报告"}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="report-stack">
      {keyFrameError ? (
        <div className="report-media-error">
          <span>{keyFrameError}</span>
          <button type="button" onClick={onRetryKeyFrames}>重新读取关键帧</button>
        </div>
      ) : null}
      <FrameReport
        title="发力启动"
        description={analysis.start_frame}
        imageAlt="发力启动关键帧"
        imageLoading={keyFramesLoading}
        imageUrl={assetUrls.key_frame_start}
        onRetryImage={onRetryKeyFrames}
      />
      <FrameReport
        title="挥拍击球"
        description={analysis.impact_frame}
        imageAlt="挥拍击球关键帧"
        imageLoading={keyFramesLoading}
        imageUrl={assetUrls.key_frame_impact}
        onRetryImage={onRetryKeyFrames}
      />
      <FrameReport
        title="结束随挥"
        description={analysis.end_frame}
        imageAlt="结束随挥关键帧"
        imageLoading={keyFramesLoading}
        imageUrl={assetUrls.key_frame_end}
        onRetryImage={onRetryKeyFrames}
      />
    </div>
  );
}

function FrameReport({
  description,
  imageAlt,
  imageLoading,
  imageUrl,
  onRetryImage,
  title,
}: {
  description?: TeacherWebQwenDescription | null;
  imageAlt: string;
  imageLoading: boolean;
  imageUrl?: string;
  onRetryImage: () => void;
  title: string;
}) {
  if (!description) return null;

  return (
    <section className="report-section">
      <h4>{title}</h4>
      <ReportKeyFrameMedia
        alt={imageAlt}
        isLoading={imageLoading}
        onRetry={onRetryImage}
        url={imageUrl}
      />
      <ReportList title="优点" values={description.strengths} />
      <ReportList title="需改进" values={description.weaknesses} />
      <ReportList title="改进建议" values={description.improvements} />
      <ReportList title="训练计划" values={description.training_plan} />
    </section>
  );
}

function ReportKeyFrameMedia({
  alt,
  isLoading,
  onRetry,
  url,
}: {
  alt: string;
  isLoading: boolean;
  onRetry: () => void;
  url?: string;
}) {
  const [failedUrl, setFailedUrl] = useState("");
  const hasFailed = Boolean(url && failedUrl === url);

  if (hasFailed) {
    return (
      <div className="report-keyframe-loading error">
        <span>关键帧加载失败</span>
        <button type="button" onClick={onRetry}>重新读取</button>
      </div>
    );
  }
  if (url) {
    return (
      <img
        className="report-keyframe-image"
        src={url}
        alt={alt}
        referrerPolicy="no-referrer"
        onError={() => setFailedUrl(url)}
      />
    );
  }
  if (isLoading) {
    return <div className="report-keyframe-loading"><i aria-hidden="true" /><span>正在读取关键帧</span></div>;
  }
  return <div className="report-keyframe-loading error"><span>关键帧暂不可用</span></div>;
}

function ReportList({ title, values }: { title: string; values?: string[] | null }) {
  if (!values || values.length === 0) return null;

  return (
    <div className="report-list">
      <span>{title}</span>
      <ul>
        {values.map((item, index) => (
          <li key={`${title}-${index}`}><MarkdownText text={item} /></li>
        ))}
      </ul>
    </div>
  );
}

function MarkdownText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<strong key={`${match.index}-${match[1]}`}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
}

function InfoDialog({ children, onClose, title }: { children: ReactNode; onClose: () => void; title: string }) {
  return (
    <div className="student-logout-dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        aria-labelledby="result-info-title"
        aria-modal="true"
        className="student-logout-dialog result-info-dialog"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="result-info-title">{title}</h3>
        <div>{children}</div>
        <div className="student-logout-actions">
          <button className="student-logout-primary" type="button" onClick={onClose}>
            知道了
          </button>
        </div>
      </div>
    </div>
  );
}

function MetricExplanationDialog({
  description,
  onClose,
  title,
}: {
  description: string;
  onClose: () => void;
  title: string;
}) {
  return (
    <div className="result-explanation-backdrop" role="presentation" onClick={onClose}>
      <div
        aria-labelledby="result-metric-explanation-title"
        aria-modal="true"
        className="result-explanation-dialog"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h3 id="result-metric-explanation-title">{title}</h3>
          <button autoFocus aria-label="关闭" type="button" onClick={onClose}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m7 7 10 10" />
              <path d="m17 7-10 10" />
            </svg>
          </button>
        </header>
        <div className="result-explanation-copy">
          <strong>说明</strong>
          <p>{description}</p>
        </div>
      </div>
    </div>
  );
}

function ResultErrorCard({ message }: { message: string }) {
  return (
    <article className="result-card result-error-card">
      <strong>{message}</strong>
      <button className="ghost-button" type="button" onClick={() => window.history.back()}>
        返回上一页
      </button>
    </article>
  );
}

function validCompData(value: unknown): TeacherWebCompData | null {
  return isRecord(value) ? value as TeacherWebCompData : null;
}

function validLlmAnalysis(value: unknown): TeacherWebLlmAnalysis | null {
  if (!isRecord(value)) return null;
  if (!optionalStringArray(value.summary)) return null;
  if (!optionalQwenDescription(value.start_frame)) return null;
  if (!optionalQwenDescription(value.impact_frame)) return null;
  if (!optionalQwenDescription(value.end_frame)) return null;

  const hasContent = Array.isArray(value.summary)
    || isRecord(value.start_frame)
    || isRecord(value.impact_frame)
    || isRecord(value.end_frame);
  return hasContent ? value as TeacherWebLlmAnalysis : null;
}

function optionalQwenDescription(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (!isRecord(value)) return false;
  return optionalStringArray(value.strengths)
    && optionalStringArray(value.weaknesses)
    && optionalStringArray(value.improvements)
    && optionalStringArray(value.training_plan);
}

function optionalStringArray(value: unknown): boolean {
  return value === undefined
    || value === null
    || (Array.isArray(value) && value.every((item) => typeof item === "string"));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function buildChartItems(firstData: TeacherWebCompData | null, secondData: TeacherWebCompData | null, side: "left" | "right") {
  const firstStats = firstData?.frame_stats as Record<string, unknown> | undefined;
  const secondStats = secondData?.frame_stats as Record<string, unknown> | undefined;
  const availableDefinitions = chartDefinitions.filter((definition) => definition.key.endsWith(`_${side}`) || isCenterMetric(definition.key));

  return availableDefinitions.map((definition) => {
    const seriesFirst = extractChartSeries(firstStats?.[definition.key]);
    const seriesSecond = extractChartSeries(secondStats?.[definition.key]);
    const maxFrameCount = Math.max(seriesFirst?.original.length ?? 0, seriesSecond?.original.length ?? 0);
    return {
      definition,
      keyFramesFirst: extractKeyFrames(firstData?.key_frames),
      keyFramesSecond: extractKeyFrames(secondData?.key_frames),
      maxFrameCount,
      seriesFirst,
      seriesSecond,
    };
  }).filter((item) => item.maxFrameCount > 1);
}

function extractChartSeries(raw: unknown): ChartSeries | null {
  if (!Array.isArray(raw)) return null;

  const toNumberSeries = (value: unknown): number[] => {
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is number => typeof item === "number" && Number.isFinite(item));
  };

  const directSeries = toNumberSeries(raw);
  if (directSeries.length > 0) {
    return { mean: null, original: directSeries, standardDeviation: null };
  }

  const originalSeries = toNumberSeries(raw[0]);
  if (originalSeries.length === 0) return null;

  const mean = toNumberSeries(raw[1]);
  const standardDeviation = toNumberSeries(raw[2]);
  return {
    mean: mean.length > 0 ? mean : null,
    original: originalSeries,
    standardDeviation: standardDeviation.length > 0 ? standardDeviation : null,
  };
}

function preferredChartSide(techType?: string | null): "left" | "right" {
  return techType?.startsWith("left_") ? "left" : "right";
}

function isCenterMetric(key: string) {
  return key === "trunk_flexion_angle" || key === "trunk_lateral_tilt_angle" || key === "trunk_rotation_angle" || key === "center_of_gravity";
}

function extractKeyFrames(value?: number[]): number[] {
  if (!Array.isArray(value)) return [];
  return value.filter((frame) => Number.isInteger(frame) && frame >= 0);
}

function standardRangeValues(series: ChartSeries | null): { lower: number[]; upper: number[] } {
  if (!series?.mean || !series.standardDeviation) return { lower: [], upper: [] };
  const count = Math.min(series.mean.length, series.standardDeviation.length);
  const lower: number[] = [];
  const upper: number[] = [];

  for (let index = 0; index < count; index += 1) {
    const mean = series.mean[index];
    const deviation = series.standardDeviation[index];
    const first = mean - 2 * deviation;
    const second = mean + 2 * deviation;
    if (!Number.isFinite(first) || !Number.isFinite(second)) continue;
    lower.push(Math.min(first, second));
    upper.push(Math.max(first, second));
  }
  return { lower, upper };
}

function chartInspection(item: ChartItem, selectedFrame: number) {
  const progress = clamp(selectedFrame, 0, item.maxFrameCount - 1) / Math.max(1, item.maxFrameCount - 1);
  const primaryValue = sampledSeriesValue(item.seriesFirst?.original, progress);
  const referenceValue = sampledSeriesValue(item.seriesSecond?.original, progress);
  const mean = sampledSeriesValue(item.seriesSecond?.mean ?? undefined, progress);
  const deviation = sampledSeriesValue(item.seriesSecond?.standardDeviation ?? undefined, progress);
  const standardRange = mean !== null && deviation !== null
    ? [Math.min(mean - 2 * deviation, mean + 2 * deviation), Math.max(mean - 2 * deviation, mean + 2 * deviation)] as const
    : null;
  return { primaryValue, referenceValue, standardRange };
}

function sampledSeriesValue(values: number[] | undefined, progress: number): number | null {
  if (!values?.length) return null;
  const index = Math.round(clamp(progress, 0, 1) * Math.max(0, values.length - 1));
  const value = values[index];
  return Number.isFinite(value) ? value : null;
}

function formatMetricValue(value: number | null, definition: ChartDefinition): string {
  if (value === null) return "—";
  const number = definition.key === "center_of_gravity" ? value.toFixed(3) : value.toFixed(1);
  return `${number} ${definition.unit}`;
}

function formatMetricRange(range: readonly [number, number], definition: ChartDefinition): string {
  const digits = definition.key === "center_of_gravity" ? 3 : 1;
  return `${range[0].toFixed(digits)}–${range[1].toFixed(digits)} ${definition.unit}`;
}

function sampledPoint(points: Array<[number, number]>, progress: number): [number, number] | null {
  if (points.length === 0) return null;
  const index = Math.round(clamp(progress, 0, 1) * Math.max(0, points.length - 1));
  return points[index] ?? null;
}

function smoothPath(points: Array<[number, number]>): string {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0][0]} ${points[0][1]}`;

  let path = `M ${points[0][0].toFixed(2)} ${points[0][1].toFixed(2)}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const previous = points[Math.max(0, index - 1)];
    const current = points[index];
    const next = points[index + 1];
    const following = points[Math.min(points.length - 1, index + 2)];
    const control1X = current[0] + (next[0] - previous[0]) / 6;
    const control1Y = current[1] + (next[1] - previous[1]) / 6;
    const control2X = next[0] - (following[0] - current[0]) / 6;
    const control2Y = next[1] - (following[1] - current[1]) / 6;
    path += ` C ${control1X.toFixed(2)} ${control1Y.toFixed(2)}, ${control2X.toFixed(2)} ${control2Y.toFixed(2)}, ${next[0].toFixed(2)} ${next[1].toFixed(2)}`;
  }
  return path;
}

function areaPath(upper: Array<[number, number]>, lower: Array<[number, number]>): string {
  if (upper.length < 2 || lower.length < 2) return "";
  const upperPath = smoothPath(upper);
  const reversedLower = [...lower].reverse();
  const lowerPath = smoothPath(reversedLower).replace(/^M/, "L");
  return `${upperPath} ${lowerPath} Z`;
}

function formatAxisValue(value: number, key: string, span: number): string {
  if (key === "center_of_gravity") return value.toFixed(1);
  return value.toFixed(span < 8 ? 1 : 0);
}

function formatProgress(progress: number): string {
  if (progress === 0 || progress === 1) return progress.toFixed(0);
  if (progress === 0.5) return progress.toFixed(1);
  return progress.toFixed(2);
}

function visualizationNodeNameForMetric(key: string): string | null {
  const nodeNames: Record<string, string> = {
    center_of_gravity: "CenterOfGravityVisualization",
    elbow_angle_left: "LeftElbowVisualization",
    elbow_angle_right: "RightElbowVisualization",
    hip_angle_left: "LeftHipAngleVisualization",
    hip_angle_right: "RightHipAngleVisualization",
    hip_rotation_left: "LeftHipRotationVisualization",
    hip_rotation_right: "RightHipRotationVisualization",
    knee_angle_left: "LeftKneeAngleVisualization",
    knee_angle_right: "RightKneeAngleVisualization",
    shoulder_arm_angle_left: "LeftShoulderArmVisualization",
    shoulder_arm_angle_right: "RightShoulderArmVisualization",
    shoulder_hip_angle_left: "LeftShoulderHipVisualization",
    shoulder_hip_angle_right: "RightShoulderHipVisualization",
    shoulder_rotation_left: "LeftShoulderVisualization",
    shoulder_rotation_right: "RightShoulderVisualization",
    trunk_flexion_angle: "TrunkFlexionVisualization",
    trunk_lateral_tilt_angle: "TrunkLateralTiltVisualization",
    trunk_rotation_angle: "TrunkRotationVisualization",
  };
  return nodeNames[key] ?? null;
}

function frameFromProgress(time: number, duration: number, frameCount: number): number {
  if (duration <= 0 || frameCount <= 1) return 0;
  return clamp(Math.round((time / duration) * (frameCount - 1)), 0, frameCount - 1);
}

function getLastTeacherToken(): string | null {
  try {
    return window.localStorage.getItem(lastTeacherTokenKey);
  } catch {
    return null;
  }
}

function statusText(status?: string, error?: string): string {
  if (error) return "读取失败";
  switch (status) {
    case "comp completed":
      return "已完成";
    case "comp failed":
      return "分析失败";
    case "started":
      return "分析中";
    default:
      return "读取中";
  }
}

function statusTone(status?: string, error?: string): string {
  if (error || status === "comp failed") return "error";
  if (status === "comp completed") return "success";
  return "running";
}

function formatShortTime(time: number): string {
  if (!Number.isFinite(time) || time < 0) return "0:00";
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
